import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'
import useFormal from '@kevinwolf/formal-web'
import { useTranslation } from 'react-i18next'
import { SubmitButton } from 'components/elements/SubmitButton'
import { useMessage } from 'components/elements/Message'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import { formatDateFieldToDate, normalizePlate, yupDateField } from 'utils'
import debounce from 'debounce'
import { useClasses } from '../MyReservations.css'
import SelectField from 'Elements/SelectField'
import { IUserReservationsOffersOfferData, useApi } from 'components/providers/ApiProvider'
import { DateField } from 'Elements/DateField'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import { useMyReservations } from 'components/views/my-reservations/MyReservationsProvider'
import { addDays, startOfDay } from 'date-fns'

export type Props = {}
export type Functions = {}
export type AllProps = Props & Functions

const Toolbar: FunctionComponent<AllProps> = ({}) => {
  const classes = useClasses()
  const api = useApi()
  const { t, i18n } = useTranslation()
  const { showSuccess, showError } = useMessage()

  const { setLoadList } = useMyReservations()

  const [offers, setOffers] = useState<IUserReservationsOffersOfferData[]>([])
  const [plateNumberFieldOptions, setPlateNumberFieldOptions] = useState<string[]>([])

  useEffect(() => {
    api.getUserReservationsOffers()
      .then(data => {
        setOffers(data.offers)
      })
      .catch(() => {
      })
  }, [])

  useEffect(() => {
    if (offers.length == 1) {
      formal.change('offer', offers[0].id)
    }
  }, [offers])

  const initialValues = {
    offer: 0,
    date: '',
    plateNumber: '',
  }

  const schema = yup.object().shape({
    offer: yup.number().required().min(1),
    date: yupDateField({ required: true }),
    plateNumber: yup.string().required(),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      await api.makeUserReservation({
        offer_id: values.offer,
        date: formatDateFieldToDate(values.date),
        plate_number: values.plateNumber.toUpperCase(),
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          formal.change('date', '')
          formal.change('plateNumber', '')

          setLoadList()

          showSuccess(t('messages.userReservationMade'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            case 720:
              message = t('alerts.reservationOverlap')
              break
            case 721:
              message = t('alerts.maxReservationsReached')
              break
            case 723:
              message = t('alerts.maxUserReservationsReached')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotMakeUserReservation')} ${message}`)
        })
    },
  })

  const fetch = useMemo(
    () =>
      debounce((params: { offer_id: number, plate_number: string }, callback: (plate_numbers: string[]) => void) => {
        api.getUserReservationPlateNumbers(params)
          .then(data => {
            callback(data.plate_numbers)
          })
          .catch(() => {
          })
      }, 500),
    [],
  )

  useEffect(() => {
    let mounted = true

    if (!formal.values.offer || !formal.values.plateNumber) {
      fetch.clear()
      setPlateNumberFieldOptions([])

      return
    }

    fetch({ offer_id: formal.values.offer, plate_number: formal.values.plateNumber }, (plate_numbers: string[]) => {
      if (mounted) {
        setPlateNumberFieldOptions(plate_numbers)
      }
    })

    return () => {
      mounted = false
    }
  }, [formal.values.plateNumber])

  return (
    <Box className={classes.toolbarContainer}>
      <form {...formal.getFormProps()} noValidate>
        <Grid container direction="column" spacing={2}>
          <SelectField
            GridProps={{ className: classes.offerFieldWrapper }}
            label={t('zone')}
            optionsCustom={[
              {
                label: offers.length > 1 ? t('select') : '',
                id: 0,
              },
              ...offers.map(offer => {
                const zone = offer.zone
                const localization = zone.localizations.find(l => l.language == i18n.language)

                return {
                  label: `${zone.code}${localization && localization.name ? ` ${localization.name}` : ''}`,
                  id: offer.id,
                }
              }),
            ]}
            required
            {...formal.getFieldProps('offer')}
            disabled={offers.length == 1 || formal.isSubmitting}
          />
          <DateField
            GridProps={{ className: classes.dateFieldWrapper }}
            label={t('date')}
            required
            disablePast
            {
              ...(offers.length && offers.find(o => o.id == formal.values.offer) && offers.find(o => o.id == formal.values.offer).type.window_period && {
                maxDate: startOfDay(addDays(Date.now(), offers.find(o => o.id == formal.values.offer).type.window_period - 1)),
              })
            }
            {...formal.getFieldProps('date')}
          />
          <Grid container item spacing={1}>
            <Grid item className={classes.plateNumberFieldWrapper}>
              <Autocomplete
                value={null}
                inputValue={formal.values.plateNumber}
                onInputChange={(event, value) => {
                  const normalizedValue = normalizePlate(value)

                  formal.change('plateNumber', normalizedValue)

                  if (value.toUpperCase() == normalizedValue) {
                    return
                  }

                  const target = event.target as HTMLInputElement
                  const valueLengthDiff = normalizedValue.length - value.length
                  const selectionStart = target.selectionStart + valueLengthDiff
                  const selectionEnd = target.selectionEnd + valueLengthDiff

                  setTimeout(target => {
                    target.selectionStart = selectionStart
                    target.selectionEnd = selectionEnd
                  }, 0, target)
                }}
                options={plateNumberFieldOptions}
                freeSolo
                disableClearable
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('plateNumber')}
                    variant="outlined"
                    required
                    inputProps={{
                      ...params.inputProps,
                      maxLength: 10,
                      style: {
                        textTransform: 'uppercase',
                        fontFamily: '"Roboto Mono", monospace',
                        lineHeight: '18px',
                      },
                    }}
                    disabled={formal.isSubmitting}
                    error={!!formal.errors.plateNumber}
                  />
                )}
              />
            </Grid>
            <SubmitButton
              buttonText={t('reserve')}
              disabled={formal.isSubmitting}
            />
          </Grid>
        </Grid>
      </form>
    </Box>
  )
}

export default Toolbar
